import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NoxusInputField } from "../Form/InputField";
import { Plus } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { DeleteModal } from "../Modals/DeleteModal";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import {
  ActionToolSettingsInput,
  AgentToolSettingsInput,
  Assistant,
  AssistantsService,
  AttachFileSettings,
  HumanInTheLoopSettingsInput,
  KnowledgeBaseQaSettingsInput,
  KnowledgeBaseSelectorSettingsInput,
  NoxusQaSettingsInput,
  SafetyService,
  WebResearchSettingsInput,
  WorkflowSettingsInput,
  MemorySettingsInput,
  FileSystemSettingsInput,
  SubAgentSettingsInput,
  AgentMemorySettingsInput,
} from "@/generated";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  DefaultValues,
  FormProvider,
  useForm,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import { useAtomValue } from "jotai";
import { cloneDeep, debounce } from "lodash";
import { ConfigFormRenderer } from "../Form/ConfigFormRenderer";
import { NoxusFormEntryConfig } from "../Spotflow/ConfigDrawer/ConfigDrawer";
import { NoxusIconPickerField } from "../Flows/NoxusIconPicker";
import { cn } from "@/lib/utils";
import { useDisclosure } from "@/utils/useDisclosure";
import { Separator } from "../shadcn/separator";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import {
  AssistantEditorSaveState as SaveState,
  flushAgentSaveSignalAtom,
  agentFormRefreshAtom,
} from "@/store/assistant-editor-jotai";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";
import { useGenieContext } from "@/lib/genie/use-genie-context";
import { extractImageUri } from "@/utils/generic";
import { keepUnsavedEdits } from "./keepUnsavedEdits";
import { AGENT_TAB_ORDER } from "@/components/Form/agentTabOrder";
import {
  MAX_ENTITY_NAME,
  MAX_ENTITY_DESCRIPTION,
} from "@/constants/entityLimits";

type TypedAssistant = Omit<Assistant, "definition"> & {
  definition: IDefinition;
};

const DEFAULT_ICON = "coworker";

function formDefaults(assistant: Assistant): DefaultValues<TypedAssistant> {
  return {
    ...assistant,
    image_url: (assistant?.image_url as string) || "coworker",
    definition: {
      ...assistant.definition,
    },
  };
}

export const AssistantDetailsContent = ({
  assistant,
  onSaveStateChange,
  selectedVersionId,
}: {
  assistant: Assistant;
  onSaveStateChange?: (state: SaveState) => void;
  selectedVersionId?: string;
}) => {
  const { spotGroup } = useSpotUser();

  const queryClient = useQueryClient();
  const defaultValues = useMemo(() => formDefaults(assistant), [assistant]);

  const form = useForm<TypedAssistant>({ defaultValues });

  const {
    watch,
    formState: { dirtyFields },
    reset,
    getValues,
  } = form;

  // What the server holds for this agent now. Genie's edits and capability
  // updates refetch it before they signal, so the query cache already has it,
  // but the assistant prop can still be the agent from before for a render:
  // read the cache, or the form takes the old agent and its next autosave
  // writes it back over the change (DEV-3016).
  const serverValues = (): TypedAssistant => {
    const cached = queryClient.getQueryData<Assistant>([
      DATA_KEYS.ASSISTANT_ID,
      { path: { group_id: spotGroup.id, assistant_id: assistant.id } },
    ]);
    return (cached ? formDefaults(cached) : defaultValues) as TypedAssistant;
  };

  // What the server last confirmed: the values the form opened with, then each
  // save. What differs from it is what the user has not saved yet. Deep
  // copies: getValues() is shallow, and typing writes into the form's nested
  // definition in place, so a shared one would always look saved.
  const lastSavedRef = useRef<TypedAssistant>(cloneDeep(getValues()));
  const submittedRef = useRef<TypedAssistant | null>(null);

  // Genie or a capability dialog changed this agent: take its values, but keep
  // what the user typed that autosave has not sent yet. The signal keeps its
  // last value: one from before this editor mounted (another agent, or this
  // one before a version was opened) is not for it, and a version view is
  // read-only, so nothing resets it to the live agent.
  const genieChange = useAtomValue(agentFormRefreshAtom);
  const genieChangeAtMount = useRef(genieChange);
  useEffect(() => {
    if (
      !genieChange ||
      genieChange === genieChangeAtMount.current ||
      genieChange.agentId !== assistant.id ||
      selectedVersionId
    )
      return;
    const server = serverValues();
    // An edit kept from the user stays unsaved (dirty), so the effect that
    // syncs the name and icon from the server leaves it alone.
    if (genieChange.remount) reset(server);
    else
      reset(keepUnsavedEdits(server, getValues(), lastSavedRef.current), {
        keepDirty: true,
      });
    lastSavedRef.current = cloneDeep(server);
    // A save sent before Genie's change can land after it and write the old
    // agent back: once it lands, send the form again, which holds the change.
    if (isSavingRef.current) {
      savedOverGenieRef.current = true;
      hasPendingChangesRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genieChange]);

  const isSavingRef = useRef(false);
  const hasPendingChangesRef = useRef(false);
  const savedOverGenieRef = useRef(false);
  const handleSubmitRef = useRef<(data: TypedAssistant) => void>(() => {});

  useEffect(() => {
    if (isSavingRef.current) {
      return;
    }

    const formValues = getValues();
    const updates: Partial<TypedAssistant> = {};

    const serverIcon = extractImageUri(assistant.image_url) ?? DEFAULT_ICON;
    const formIcon = extractImageUri(formValues.image_url) ?? DEFAULT_ICON;
    if (!dirtyFields.image_url && serverIcon !== formIcon) {
      updates.image_url = serverIcon;
    }

    if (!dirtyFields.name && assistant.name !== formValues.name) {
      updates.name = assistant.name;
    }

    if (Object.keys(updates).length > 0) {
      reset({ ...formValues, ...updates });
    }
  }, [
    assistant.name,
    assistant.image_url,
    dirtyFields.name,
    dirtyFields.image_url,
    getValues,
    reset,
  ]);

  const deleteModalControls = useDisclosure();
  const router = useRouter();

  const deleteAssistantMutation = useMutation({
    mutationFn: AssistantsService.deleteAssistant,
    onSuccess: () => {
      analytics_track(ANALYTICS_EVENTS.COWORKER_DELETE, {
        assistant_id: assistant.id,
      });

      router.push("/assistant");
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.ASSISTANT_ID, assistant.id],
      });
    },
    onError: () => {
      SpotToast.error("Failed to delete agent");
    },
  });

  const handleDeleteAssistant = async () => {
    deleteAssistantMutation.mutate({
      path: {
        group_id: spotGroup.id,
        assistant_id: assistant.id,
      },
    });
  };

  // View-only viewers (resource:read without resource:edit) can't save
  // changes — the form fields are disabled, but we also short-circuit
  // the mutation in case a stale autosave fires.
  const isReadOnly = useEditorReadOnly();

  const updateAssistantMutation = useMutation({
    mutationFn: (data: TypedAssistant) => {
      return AssistantsService.updateAssistant({
        body: data as any,
        path: {
          group_id: spotGroup.id,
          assistant_id: assistant.id,
        },
      });
    },

    onSuccess: () => {
      const changedFields: string[] = [];

      if (dirtyFields.name) changedFields.push("name");
      if (dirtyFields.description) changedFields.push("description");
      if (dirtyFields.image_url) changedFields.push("image_url");

      if (dirtyFields.definition) {
        Object.keys(dirtyFields.definition).forEach((key) => {
          changedFields.push(`definition.${key}`);
        });
      }

      analytics_track(ANALYTICS_EVENTS.COWORKER_EDIT, {
        assistant_id: assistant.id,
        changed_fields: changedFields,
        changed_fields_count: changedFields.length,
      });

      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.ASSISTANT_ID],
      });
      if (
        changedFields.includes("name") ||
        changedFields.includes("image_url")
      ) {
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.ASSISTANTS],
        });
      }

      // A save Genie's change overtook confirmed the old agent, not the one
      // the form now holds; the re-send below confirms that.
      if (submittedRef.current && !savedOverGenieRef.current)
        lastSavedRef.current = submittedRef.current;
      savedOverGenieRef.current = false;
      isSavingRef.current = false;
      if (hasPendingChangesRef.current) {
        hasPendingChangesRef.current = false;
        handleSubmitRef.current(form.getValues());
      } else {
        onSaveStateChange?.(SaveState.SAVED);
      }
    },

    onError: () => {
      isSavingRef.current = false;
      hasPendingChangesRef.current = false;
      onSaveStateChange?.(SaveState.ERROR);
      SpotToast.error("Error updating agent");
    },
  });

  const handleSubmit = useCallback(
    (data: TypedAssistant) => {
      if (selectedVersionId || isReadOnly) {
        return;
      }

      const image_url = extractImageUri(data.image_url) ?? data.image_url;

      isSavingRef.current = true;
      hasPendingChangesRef.current = false;
      submittedRef.current = cloneDeep(data);
      onSaveStateChange?.(SaveState.SAVING);
      updateAssistantMutation.mutate({ ...data, image_url });
    },
    [onSaveStateChange, selectedVersionId, isReadOnly, updateAssistantMutation]
  );

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // Reads the form when it fires, not when it was scheduled: Genie can
  // change the agent in between, and the save must carry that change.
  const debouncedSubmit = useMemo(
    () =>
      debounce(() => {
        if (isSavingRef.current) {
          hasPendingChangesRef.current = true;
          return;
        }
        handleSubmitRef.current(form.getValues());
      }, 1000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (selectedVersionId || isReadOnly) {
      return;
    }

    const subscription = watch((_values, { name }) => {
      // A reset loads what the server holds: nothing to save.
      if (name === undefined) return;
      onSaveStateChange?.(SaveState.PENDING_SAVE);
      if (isSavingRef.current) {
        hasPendingChangesRef.current = true;
        return;
      }
      debouncedSubmit();
    });

    return () => {
      subscription.unsubscribe();
      debouncedSubmit.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, selectedVersionId, isReadOnly]);

  const flushAgentSaveSignal = useAtomValue(flushAgentSaveSignalAtom);
  useEffect(() => {
    if (flushAgentSaveSignal === 0) return;
    debouncedSubmit.flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushAgentSaveSignal]);

  return (
    <div className="w-full">
      <div className="flex">
        <form
          style={{ width: "100%" }}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FormProvider {...form}>
            <DefinitionEditor
              assistantId={assistant.id}
              form={form}
              selectedVersionId={selectedVersionId}
              isReadOnly={isReadOnly}
            />
          </FormProvider>
        </form>
      </div>
      <DeleteModal
        isOpen={deleteModalControls.isOpen}
        onClose={deleteModalControls.onClose}
        onDelete={handleDeleteAssistant}
        title="Delete Agent"
        description="Are you sure you want to delete this agent?"
      />
    </div>
  );
};

const DefinitionEditor = ({
  assistantId,
  form,
  selectedVersionId,
  isReadOnly,
}: {
  assistantId: string;
  form: UseFormReturn<TypedAssistant>;
  selectedVersionId?: string;
  isReadOnly?: boolean;
}) => {
  // Read-only locks fields just like a published version does.
  const readOnly = !!selectedVersionId || !!isReadOnly;
  // The form has to be the shape this agent is pinned to, not the newest one:
  // a newer version may drop an option or move a default, and rendering that
  // would offer settings the agent doesn't have. Absent means unpinned, which
  // is v1.
  // Scoped subscription, not form.watch: watch() on the host re-renders the
  // whole DefinitionEditor — every tool and setting — on any keystroke
  // anywhere in the form, to read one field.
  const pins = useWatch({
    control: form.control,
    name: "definition.capability_versions",
  }) as Record<string, number> | undefined;
  // The whole pin map, not just the settings version: a tool capability's
  // version decides which of its options the form may offer, and the server
  // is what knows that. Sorted so two identical maps share one cache entry.
  const pinQuery = Object.entries(pins ?? {})
    .map(([name, version]) => `${name}:${version}`)
    .sort()
    .join(",");
  const { data: agentData } = useQuery({
    queryKey: [DATA_KEYS.AGENT_FORM, pinQuery],
    queryFn: () => SafetyService.getCoworkerForm({ query: { pins: pinQuery } }),
  });

  const agentFormData = agentData?.data as Record<string, NoxusFormEntryConfig>;

  // The open section lives in the URL, so a link (or Genie) can open it.
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const section = searchParams?.get("section");
  const openSection = (next: string) => {
    const params = new URLSearchParams(searchParams?.toString());
    params.set("section", next);
    router.replace(`${pathname}?${params.toString()}`);
  };

  useGenieContext("current_agent", "The agent currently being edited", {
    id: assistantId,
    name: form.watch("name"),
    model: form.watch("definition.model"),
  });

  return (
    <div>
      <div className="flex flex-col gap-6">
        <div className="flex justify-center items-center gap-4">
          <NoxusIconPickerField
            name="image_url"
            control={form.control}
            size="xl"
            disabled={readOnly}
            defaultIcon="coworker"
          />

          <NoxusInputField
            name="name"
            control={form.control}
            label="Name"
            placeholder="Name your agent"
            maxLength={MAX_ENTITY_NAME}
            isReadOnly={readOnly}
          />
        </div>
        <NoxusInputField
          name="description"
          control={form.control}
          label="Description"
          placeholder="Add a short description about this agent"
          helperText="Doesn't affect the agent's behavior but helps other tools interpret it more accurately."
          maxLength={MAX_ENTITY_DESCRIPTION}
          isTextarea
          customStyle="h-[5rem]"
          isReadOnly={readOnly}
          disable={readOnly}
        />
        <Separator className="my-1.5" />
        <ConfigFormRenderer
          config={agentFormData || {}}
          form={form}
          fieldPrefix="definition"
          isReadOnly={readOnly}
          tabOrder={AGENT_TAB_ORDER}
          tab={section ?? undefined}
          onTabChange={openSection}
        />
      </div>
    </div>
  );
};

// forwardRef + prop spread so this can serve as a Radix `asChild` trigger
// (e.g. wrapped in NoxusMenu): the menu needs the trigger's ref and injected
// pointer/keyboard handlers to open. Plain function components drop them.
export const AddNewEntry = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & {
    label: string;
    disabledLabel?: string;
    isDisabled?: boolean;
  }
>(({ label, disabledLabel, isDisabled, className, onClick, ...rest }, ref) => {
  return (
    <div
      ref={ref}
      {...rest}
      className={cn(
        "flex gap-2 items-center justify-between px-2 py-1.5 border rounded-md w-full transition-shadow",
        isDisabled
          ? "opacity-50 cursor-not-allowed"
          : "opacity-100 cursor-pointer hover:shadow-sm",
        className
      )}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      onKeyDown={(event) => {
        if (isDisabled || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        // A div never synthesises activation from a key the way a button does.
        // Menus (Radix) open on pointerdown, plain rows act on click, and this
        // component is used both ways — so raise both and let the wiring in
        // use respond to whichever it listens for.
        event.currentTarget.dispatchEvent(
          new MouseEvent("pointerdown", { bubbles: true, cancelable: true })
        );
        event.currentTarget.click();
      }}
    >
      <div className="flex gap-2 items-center">
        <div className="flex bg-accent text-muted-foreground p-1.5 rounded-md justify-center items-center">
          <Plus width={16} height={16} />
        </div>

        <p className="text-sm ">
          {isDisabled && disabledLabel ? disabledLabel : label}
        </p>
      </div>
    </div>
  );
});
AddNewEntry.displayName = "AddNewEntry";

export type Tool =
  | ActionToolSettingsInput
  | NoxusQaSettingsInput
  | WebResearchSettingsInput
  | KnowledgeBaseSelectorSettingsInput
  | KnowledgeBaseQaSettingsInput
  | WorkflowSettingsInput
  | AttachFileSettings
  | HumanInTheLoopSettingsInput
  | AgentToolSettingsInput
  | MemorySettingsInput
  | FileSystemSettingsInput
  | SubAgentSettingsInput
  | AgentMemorySettingsInput;

// A variable is just a name; the value is supplied at runtime by the embed host.
export interface VariableDefinition {
  name: string;
}

export interface IDefinition {
  tools: Tool[];
  base_tools: Tool[];
  knowledge_bases: KnowledgeBaseQaSettingsInput[];
  workflows: WorkflowSettingsInput[];
  coworkers: AgentToolSettingsInput[];
  actions: ActionToolSettingsInput[];
  variables: VariableDefinition[];
  max_tokens: number;
  temperature: number;
  model: string[];
  extra_instructions: { text: string; definition: Record<string, any> };
  [key: string]: unknown;
}
